import { Contact } from '../types/Contact';
import type { EventEntry, RelationshipLink, CompanyLink } from '../types/Entities';
import { listEvents } from '../lib/events';
import { listRelationships } from '../lib/relationships';
import { listCompaniesCore } from '@gv/core';
import { supabase } from '../lib/supabase';

export interface ContactExportData {
  contact: Contact;
  events: EventEntry[];
  relationships: RelationshipLink[];
  companies: CompanyLink[];
}

/**
 * Fetch comprehensive contact data for export
 */
export async function fetchContactExportData(contactId: string): Promise<ContactExportData> {
  const [events, relationships, companies] = await Promise.all([
    listEvents(contactId).catch(() => []),
    listRelationships(contactId).catch(() => []),
    listCompaniesCore(supabase as any, contactId).catch(() => [])
  ]);

  // Get the contact data - we'll need to fetch this from the parent component
  // For now, we'll accept it as a parameter in the export function
  return {
    contact: {} as Contact, // Will be provided by caller
    events,
    relationships,
    companies
  };
}

/**
 * Generate HTML content for PDF export optimized for AI analysis
 */
export function generateContactExportHTML(data: ContactExportData): string {
  const { contact, events, relationships, companies } = data;
  const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown Contact';
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Contact Export - ${contactName}</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          color: #333;
        }
        .header {
          text-align: center;
          border-bottom: 3px solid #2563eb;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        .contact-name {
          font-size: 2.5em;
          font-weight: bold;
          margin: 0;
          color: #1f2937;
        }
        .contact-title {
          font-size: 1.2em;
          color: #6b7280;
          margin: 5px 0;
        }
        .section {
          margin-bottom: 30px;
          padding: 20px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }
        .section-title {
          font-size: 1.4em;
          font-weight: bold;
          margin: 0 0 15px 0;
          color: #1f2937;
          border-bottom: 2px solid #f3f4f6;
          padding-bottom: 5px;
        }
        .field-group {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }
        .field {
          margin-bottom: 10px;
        }
        .field-label {
          font-weight: bold;
          color: #4b5563;
          margin-right: 8px;
        }
        .field-value {
          color: #1f2937;
        }
        .event-item, .relationship-item, .company-item {
          background: #f9fafb;
          padding: 15px;
          margin-bottom: 15px;
          border-radius: 6px;
          border-left: 4px solid #2563eb;
        }
        .event-title, .relationship-title, .company-title {
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 8px;
        }
        .event-date, .relationship-date {
          color: #6b7280;
          font-size: 0.9em;
          margin-bottom: 5px;
        }
        .notes {
          background: #f0f9ff;
          padding: 15px;
          border-radius: 6px;
          border-left: 4px solid #0ea5e9;
          margin-top: 10px;
        }
        .ai-prompt {
          background: #fef3c7;
          padding: 20px;
          border-radius: 8px;
          border-left: 4px solid #f59e0b;
          margin-top: 30px;
        }
        .tier-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.8em;
          font-weight: bold;
          text-transform: uppercase;
        }
        .tier-a { background: #dcfce7; color: #166534; }
        .tier-b { background: #dbeafe; color: #1e40af; }
        .tier-c { background: #fef3c7; color: #92400e; }
        .tier-d { background: #fee2e2; color: #991b1b; }
        .tier-unassigned { background: #f3f4f6; color: #374151; }
        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.8em;
          font-weight: bold;
          background: #e5e7eb;
          color: #374151;
        }
        @media print {
          body { margin: 0; padding: 15px; font-size: 12px; }
          .section { break-inside: avoid; margin-bottom: 20px; }
          .event-item, .relationship-item, .company-item { break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1 class="contact-name">${contactName}</h1>
        ${contact.title ? `<div class="contact-title">${contact.title}</div>` : ''}
        ${contact.company ? `<div class="contact-title">${contact.company}</div>` : ''}
        <div style="margin-top: 10px;">
          <span class="tier-badge tier-${(contact.tier || 'unassigned').toLowerCase()}">${contact.tier || 'Unassigned'}</span>
          <span class="status-badge">${contact.status || 'Not Contacted'}</span>
        </div>
        <div style="margin-top: 10px; color: #6b7280; font-size: 0.9em;">
          Export Date: ${new Date().toLocaleDateString()} | Contact ID: ${contact.id}
        </div>
      </div>

      ${generateBasicInfoSection(contact)}
      ${generateContactInfoSection(contact)}
      ${generateBusinessInfoSection(contact)}
      ${generatePersonalInfoSection(contact)}
      ${generateFinancialInfoSection(contact)}
      ${generateCompaniesSection(companies)}
      ${generateRelationshipsSection(relationships)}
      ${generateEventsSection(events)}
      ${generateNotesSection(contact)}
      ${generateAIAnalysisSection(contact)}
      ${generateAIPromptSection(contactName)}

    </body>
    </html>
  `;
}

function generateBasicInfoSection(contact: Contact): string {
  return `
    <div class="section">
      <h2 class="section-title">📋 Basic Information</h2>
      <div class="field-group">
        ${generateField('Full Name', `${contact.first_name || ''} ${contact.middle_name || ''} ${contact.last_name || ''}`.trim())}
        ${generateField('Nickname', contact.nickname)}
        ${generateField('Name Prefix', contact.name_prefix)}
        ${generateField('Name Suffix', contact.name_suffix)}
        ${generateField('Phonetic Name', `${contact.phonetic_first_name || ''} ${contact.phonetic_last_name || ''}`.trim())}
        ${generateField('Date of Birth', contact.birthday || contact.date_of_birth)}
        ${generateField('Contact Source', contact.contact_source)}
        ${generateField('Contact Type', contact.contact_type)}
        ${generateField('Industry', contact.industry)}
        ${generateField('Referral Source', contact.referral_source)}
        ${generateField('Relationship Type', contact.relationship_type)}
        ${generateField('Last Contacted', contact.last_contacted ? new Date(contact.last_contacted).toLocaleDateString() : null)}
        ${generateField('Next Follow-up', contact.next_follow_up ? new Date(contact.next_follow_up).toLocaleDateString() : null)}
      </div>
    </div>
  `;
}

function generateContactInfoSection(contact: Contact): string {
  const emails = [
    { label: contact.email_1_label || 'Primary', value: contact.email_1_value || contact.email },
    { label: contact.email_2_label || 'Secondary', value: contact.email_2_value },
    { label: contact.email_3_label || 'Other', value: contact.email_3_value }
  ].filter(item => item.value);

  const phones = [
    { label: contact.phone_1_label || 'Primary', value: contact.phone_1_value || contact.phone },
    { label: contact.phone_2_label || 'Mobile', value: contact.phone_2_value || contact.mobile },
    { label: contact.phone_3_label || 'Other', value: contact.phone_3_value }
  ].filter(item => item.value);

  const websites = [
    { label: contact.website_1_label || 'Website', value: contact.website_1_value || contact.website_url },
    { label: contact.website_2_label || 'LinkedIn', value: contact.website_2_value || contact.linkedin_url },
    { label: contact.website_3_label || 'Other', value: contact.website_3_value }
  ].filter(item => item.value);

  return `
    <div class="section">
      <h2 class="section-title">📞 Contact Information</h2>
      ${emails.length > 0 ? `
        <h3 style="margin-bottom: 10px;">Email Addresses</h3>
        ${emails.map(email => `<div class="field"><span class="field-label">${email.label}:</span><span class="field-value">${email.value}</span></div>`).join('')}
      ` : ''}
      
      ${phones.length > 0 ? `
        <h3 style="margin-bottom: 10px; margin-top: 15px;">Phone Numbers</h3>
        ${phones.map(phone => `<div class="field"><span class="field-label">${phone.label}:</span><span class="field-value">${phone.value}</span></div>`).join('')}
      ` : ''}
      
      ${websites.length > 0 ? `
        <h3 style="margin-bottom: 10px; margin-top: 15px;">Web Presence</h3>
        ${websites.map(site => `<div class="field"><span class="field-label">${site.label}:</span><span class="field-value"><a href="${site.value}" target="_blank">${site.value}</a></span></div>`).join('')}
      ` : ''}
      
      ${contact.twitter_url ? `<div class="field"><span class="field-label">Twitter:</span><span class="field-value"><a href="${contact.twitter_url}" target="_blank">${contact.twitter_url}</a></span></div>` : ''}
      ${contact.instagram_url ? `<div class="field"><span class="field-label">Instagram:</span><span class="field-value"><a href="${contact.instagram_url}" target="_blank">${contact.instagram_url}</a></span></div>` : ''}
      
      <h3 style="margin-bottom: 10px; margin-top: 15px;">Addresses</h3>
      ${generateAddressBlock('Primary', contact, '1')}
      ${generateAddressBlock('Secondary', contact, '2')}
      ${generateLegacyAddress(contact)}
    </div>
  `;
}

function generateAddressBlock(label: string, contact: Contact, suffix: string): string {
  const addressLabel = (contact as any)[`address_${suffix}_label`];
  const street = (contact as any)[`address_${suffix}_street`];
  const city = (contact as any)[`address_${suffix}_city`];
  const region = (contact as any)[`address_${suffix}_region`];
  const postal = (contact as any)[`address_${suffix}_postal_code`];
  const country = (contact as any)[`address_${suffix}_country`];
  const formatted = (contact as any)[`address_${suffix}_formatted`];
  
  if (!street && !city && !formatted) return '';
  
  const addressParts = formatted ? [formatted] : [street, city, region, postal, country].filter(Boolean);
  
  return `
    <div class="field">
      <span class="field-label">${addressLabel || label} Address:</span>
      <span class="field-value">${addressParts.join(', ')}</span>
    </div>
  `;
}

function generateLegacyAddress(contact: Contact): string {
  if (!contact.address && !contact.city) return '';
  
  const parts = [contact.address, contact.city, contact.state, contact.zip_code, contact.country].filter(Boolean);
  
  return `
    <div class="field">
      <span class="field-label">Address:</span>
      <span class="field-value">${parts.join(', ')}</span>
    </div>
  `;
}

function generateBusinessInfoSection(contact: Contact): string {
  return `
    <div class="section">
      <h2 class="section-title">🏢 Business Information</h2>
      <div class="field-group">
        ${generateField('Organization', contact.organization_name || contact.company)}
        ${generateField('Title', contact.organization_title || contact.title)}
        ${generateField('Department', contact.organization_department)}
        ${generateField('Business Name', contact.business_name)}
        ${generateField('Business Phone', contact.business_phone)}
        ${generateField('Business Email', contact.business_email)}
        ${generateField('Business Website', contact.business_website)}
        ${generateField('Business Address', contact.business_address)}
        ${generateField('Business City', contact.business_city)}
        ${generateField('Business State', contact.business_state)}
        ${generateField('Business Country', contact.business_country)}
        ${generateField('Business Postal Code', contact.business_postalcode)}
        ${generateField('Business Description', contact.business_description)}
        ${generateField('Business Size', contact.business_size)}
        ${generateField('Revenue Estimate', contact.revenue_estimate)}
      </div>
    </div>
  `;
}

function generatePersonalInfoSection(contact: Contact): string {
  return `
    <div class="section">
      <h2 class="section-title">👤 Personal Information</h2>
      <div class="field-group">
        ${generateField('Spouse Name', contact.spouse_name)}
        ${generateField('Children Names', contact.children_names)}
        ${generateField('Interests', contact.interests || contact.personal_interests)}
        ${generateField('Education', contact.education || contact.education_background)}
        ${generateField('Skills & Expertise', contact.skills_expertise)}
        ${generateField('Achievements', contact.achievements)}
        ${generateField('Location Info', contact.location_info)}
        ${generateField('Contact Preferences', contact.contact_preferences)}
        ${generateField('Marketing Consent', contact.marketing_consent ? 'Yes' : 'No')}
        ${generateField('Priority Contact', contact.is_priority ? 'Yes' : 'No')}
        ${generateField('Is Contacted', contact.is_contacted ? 'Yes' : 'No')}
      </div>
    </div>
  `;
}

function generateFinancialInfoSection(contact: Contact): string {
  return `
    <div class="section">
      <h2 class="section-title">💰 Financial & Investment Information</h2>
      <div class="field-group">
        ${generateField('Net Worth Estimate', contact.net_worth_estimate || contact.estimated_net_worth)}
        ${generateField('Net Worth Confidence', contact.net_worth_confidence)}
        ${generateField('Net Worth Reasoning', contact.net_worth_reasoning)}
        ${generateField('Annual Income Estimate', contact.estimated_annual_income)}
        ${generateField('Income Confidence', contact.income_confidence)}
        ${generateField('Income Reasoning', contact.income_reasoning)}
        ${generateField('Investment Interests', contact.investment_interests)}
        ${generateField('Investment Amount', contact.investment_amount)}
        ${generateField('Investment Capacity', contact.estimated_investment_capacity)}
        ${generateField('Accredited Investor Status', contact.accredited_investor_status || contact.accredited_investor_status_financial)}
        ${generateField('Accredited Likelihood', contact.accredited_investor_likelihood)}
        ${generateField('Financial Sophistication', contact.financial_sophistication)}
        ${generateField('Wealth Indicators', contact.wealth_indicators)}
        ${generateField('Personal Investment Score', contact.personal_investment_score)}
        ${generateField('Personal Investment Reasoning', contact.personal_investment_reasoning)}
        ${generateField('Referral Potential Score', contact.referral_potential_score)}
        ${generateField('Referral Potential Reasoning', contact.referral_potential_reasoning)}
        ${generateField('Key Factors', contact.key_factors)}
        ${generateField('Recommended Approach', contact.recommended_approach)}
        ${generateField('Industry Relevance', contact.industry_relevance)}
      </div>
      
      <h3 style="margin-top: 20px; margin-bottom: 10px;">Opportunity Information</h3>
      <div class="field-group">
        ${generateField('Opportunity Name', contact.opportunity_name)}
        ${generateField('Opportunity Status', contact.opportunity_status)}
        ${generateField('Monetary Value', contact.monetary_value)}
        ${generateField('Assigned To', contact.assigned_to)}
        ${generateField('Opportunity Source', contact.opportunity_source)}
        ${generateField('Lost Reason', contact.lost_reason)}
        ${generateField('Pipeline ID', contact.pipeline_id)}
        ${generateField('Pipeline Stage ID', contact.pipeline_stage_id)}
      </div>
    </div>
  `;
}

function generateCompaniesSection(companies: CompanyLink[]): string {
  if (companies.length === 0) return '';
  
  return `
    <div class="section">
      <h2 class="section-title">🏢 Company Associations</h2>
      ${companies.map(company => `
        <div class="company-item">
          <div class="company-title">${company.name}</div>
          ${company.roleOrTitle ? `<div><strong>Role:</strong> ${company.roleOrTitle}</div>` : ''}
          ${company.website ? `<div><strong>Website:</strong> <a href="${company.website}" target="_blank">${company.website}</a></div>` : ''}
          ${company.email ? `<div><strong>Email:</strong> ${company.email}</div>` : ''}
          ${company.phone ? `<div><strong>Phone:</strong> ${company.phone}</div>` : ''}
          ${company.startDate ? `<div><strong>Start Date:</strong> ${new Date(company.startDate).toLocaleDateString()}</div>` : ''}
          ${company.endDate ? `<div><strong>End Date:</strong> ${new Date(company.endDate).toLocaleDateString()}</div>` : ''}
        </div>
      `).join('')}
    </div>
  `;
}

function generateRelationshipsSection(relationships: RelationshipLink[]): string {
  if (relationships.length === 0) return '';
  
  return `
    <div class="section">
      <h2 class="section-title">👥 Relationships</h2>
      ${relationships.map(rel => `
        <div class="relationship-item">
          <div class="relationship-title">${rel.relatedName}</div>
          <div><strong>Relationship:</strong> ${rel.relation}</div>
          ${rel.whenMet ? `<div class="relationship-date">Met: ${new Date(rel.whenMet).toLocaleDateString()}</div>` : ''}
          ${rel.whereMet ? `<div><strong>Where Met:</strong> ${rel.whereMet}</div>` : ''}
          ${rel.notes ? `
            <div class="notes">
              <strong>Notes:</strong> ${rel.notes}
            </div>
          ` : ''}
        </div>
      `).join('')}
    </div>
  `;
}

function generateEventsSection(events: EventEntry[]): string {
  if (events.length === 0) return '';
  
  const sortedEvents = events.sort((a, b) => {
    const dateA = new Date(a.when || a.created_at || '').getTime();
    const dateB = new Date(b.when || b.created_at || '').getTime();
    return dateB - dateA; // Most recent first
  });
  
  return `
    <div class="section">
      <h2 class="section-title">📅 Events & Interactions</h2>
      ${sortedEvents.map(event => `
        <div class="event-item">
          <div class="event-title">${event.name}</div>
          ${event.when ? `<div class="event-date">${new Date(event.when).toLocaleString()}</div>` : ''}
          ${event.where ? `<div><strong>Location:</strong> ${event.where}</div>` : ''}
          ${event.whoName ? `<div><strong>With:</strong> ${event.whoName}</div>` : ''}
          ${event.participants && event.participants.length > 0 ? `
            <div><strong>Participants:</strong> ${event.participants.map(p => p.name).join(', ')}</div>
          ` : ''}
          ${event.notes ? `
            <div class="notes">
              <strong>Notes:</strong> ${event.notes}
            </div>
          ` : ''}
          ${event.tasks && event.tasks.length > 0 ? `
            <div style="margin-top: 10px;">
              <strong>Tasks:</strong>
              <ul style="margin: 5px 0;">
                ${event.tasks.map(task => `<li>${task}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
        </div>
      `).join('')}
    </div>
  `;
}

function generateNotesSection(contact: Contact): string {
  const noteFields = [
    { label: 'General Notes', value: contact.notes },
    { label: 'Private Notes', value: contact.private_notes },
    { label: 'Personal Details', value: contact.personal_details },
    { label: 'Comprehensive Notes', value: contact.comprehensive_notes },
    { label: 'Professional Experience', value: contact.professional_experience },
    { label: 'About Section', value: contact.about_section },
    { label: 'TLDR Summary', value: contact.tldr_summary },
    { label: 'LinkedIn Description', value: contact.linkedin_description },
    { label: 'AI Summary', value: contact.ai_summary }
  ].filter(field => field.value);
  
  if (noteFields.length === 0) return '';
  
  return `
    <div class="section">
      <h2 class="section-title">📝 Notes & Summary</h2>
      ${noteFields.map(field => `
        <div style="margin-bottom: 20px;">
          <h3 style="margin-bottom: 10px; color: #4b5563;">${field.label}</h3>
          <div class="notes">${field.value}</div>
        </div>
      `).join('')}
      
      ${contact.tags && contact.tags.length > 0 ? `
        <div style="margin-top: 20px;">
          <h3 style="margin-bottom: 10px; color: #4b5563;">Tags</h3>
          <div>
            ${contact.tags.map(tag => `<span style="display: inline-block; background: #e5e7eb; color: #374151; padding: 4px 8px; border-radius: 12px; font-size: 0.8em; margin-right: 8px; margin-bottom: 4px;">${tag}</span>`).join('')}
          </div>
        </div>
      ` : ''}
    </div>
  `;
}

function generateAIAnalysisSection(contact: Contact): string {
  return `
    <div class="section">
      <h2 class="section-title">🤖 AI Analysis Data</h2>
      <div class="field-group">
        ${generateField('Analysis Date', contact.analysis_date)}
        ${generateField('AI Last Updated', contact.ai_last_updated)}
        ${generateField('Parsed At', contact.parsed_at)}
        ${generateField('Summary', contact.summary)}
        ${generateField('Call Count', contact.call_count)}
        ${generateField('Meeting Count', contact.meeting_count)}
        ${generateField('Created By', contact.created_by)}
        ${generateField('Updated By', contact.updated_by)}
        ${generateField('Created At', contact.created_at ? new Date(contact.created_at).toLocaleString() : null)}
        ${generateField('Updated At', contact.updated_at ? new Date(contact.updated_at).toLocaleString() : null)}
      </div>
      
      <h3 style="margin-top: 20px; margin-bottom: 10px;">Custom Fields</h3>
      <div class="field-group">
        ${generateField(contact.custom_field_1_label || 'Custom Field 1', contact.custom_field_1_value)}
        ${generateField(contact.custom_field_2_label || 'Custom Field 2', contact.custom_field_2_value)}
        ${generateField(contact.custom_field_3_label || 'Custom Field 3', contact.custom_field_3_value)}
      </div>
    </div>
  `;
}

function generateAIPromptSection(contactName: string): string {
  return `
    <div class="ai-prompt">
      <h2 style="margin-top: 0; color: #92400e;">🎯 AI Analysis Prompt</h2>
      <p><strong>Use this comprehensive contact profile to answer questions about ${contactName}. Key areas to focus on:</strong></p>
      <ul>
        <li><strong>Relationship Mapping:</strong> Analyze connections, mutual contacts, and networking opportunities</li>
        <li><strong>Investment Profile:</strong> Assess financial capacity, investment interests, and accredited status</li>
        <li><strong>Business Intelligence:</strong> Understand their business role, industry relevance, and decision-making authority</li>
        <li><strong>Communication Strategy:</strong> Review interaction history, preferred channels, and engagement patterns</li>
        <li><strong>Opportunity Assessment:</strong> Evaluate potential for partnerships, investments, or referrals</li>
        <li><strong>Personal Insights:</strong> Leverage personal interests, family details, and relationship context</li>
        <li><strong>Next Steps:</strong> Recommend specific actions based on their tier, status, and engagement history</li>
      </ul>
      <p><em>This export was generated on ${new Date().toLocaleString()} for AI-assisted relationship management and strategic planning.</em></p>
    </div>
  `;
}

function generateField(label: string, value: any): string {
  if (value === null || value === undefined || value === '') return '';
  
  return `
    <div class="field">
      <span class="field-label">${label}:</span>
      <span class="field-value">${value}</span>
    </div>
  `;
}

/**
 * Generate and download PDF for a contact
 */
export async function exportContactToPDF(contact: Contact): Promise<void> {
  const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contact';
  
  try {
    // Fetch comprehensive data
    const exportData = await fetchContactExportData(contact.id!);
    exportData.contact = contact; // Include the contact data
    
    // Generate HTML content
    const htmlContent = generateContactExportHTML(exportData);
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Could not open print window. Please allow popups and try again.');
    }
    
    // Set window title
    printWindow.document.title = `Contact Export - ${contactName}`;
    
    // Write HTML content to the new window
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print dialog
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        // Add a message to help users
        setTimeout(() => {
          if (!printWindow.closed) {
            printWindow.focus();
          }
        }, 1000);
      }, 500);
    };
    
  } catch (error) {
    console.error('Error exporting contact to PDF:', error);
    throw new Error(`Failed to export ${contactName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Generate downloadable HTML file for a contact
 */
export async function exportContactToHTML(contact: Contact): Promise<void> {
  const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contact';
  
  try {
    // Fetch comprehensive data
    const exportData = await fetchContactExportData(contact.id!);
    exportData.contact = contact;
    
    // Generate HTML content
    const htmlContent = generateContactExportHTML(exportData);
    
    // Create filename with safe characters
    const safeFirstName = (contact.first_name || '').replace(/[^a-zA-Z0-9]/g, '_');
    const safeLastName = (contact.last_name || '').replace(/[^a-zA-Z0-9]/g, '_');
    const dateStamp = new Date().toISOString().split('T')[0];
    const filename = `${safeFirstName}_${safeLastName}_Export_${dateStamp}.html`.replace(/_+/g, '_');
    
    // Create and download file
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error exporting contact to HTML:', error);
    throw new Error(`Failed to export ${contactName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}